import { Typography, Badge, Button, Box, Tooltip, Iconography, formatPrice } from '@lucid/lego';
import type { VehicleOrder } from '../../../../api/services/lupp/order/importer/dto.importer';
import { getLineItemStatusBadgeColor, getOrderPreferenceLabel } from '../../constants';
import { isOrderConfirmationAvailable } from '../utils';
import { formatVehicleModelName } from '../../../../utils/tools';
import styles from './LineItems.module.scss';

const formatDisplayPrice = (amount: number, locale: string): string =>
  formatPrice(amount, { locale });

const getVehicleOrderStatusLabel = (status: string, labels: Record<string, string>): string => {
  const map: Record<string, string> = {
    'IN REVIEW': labels['status.inReview'] ?? 'In Review',
    'WAITING FOR CONFIRMATION':
      labels['status.waitingForConfirmation'] ?? 'Waiting for Confirmation',
    CONFIRMED: labels['status.confirmed'] ?? 'Confirmed',
    'READY FOR DISPATCH': labels['status.readyForDispatch'] ?? 'Ready for Pickup',
    FULFILLED: labels['status.fulfilled'] ?? 'Fulfilled',
    'DENIED / CANCELED': labels['status.deniedCanceled'] ?? 'Denied / Canceled',
    DENIED: labels['status.deniedCanceled'] ?? 'Denied / Canceled',
  };
  return map[status.toUpperCase()] ?? status.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
};

/**
 * Returns the importer-facing definition for a line item status, used as a
 * tooltip when hovering the status badge. Returns null when no definition
 * exists for the given status.
 */
const getVehicleOrderStatusDefinition = (
  status: string,
  labels: Record<string, string>
): string | null => {
  const map: Record<string, string> = {
    'IN REVIEW': labels['status.definition.inReview'] ?? 'Order pending VIN assignment',
    'WAITING FOR CONFIRMATION':
      labels['status.definition.waitingForConfirmation'] ??
      'Order pending VIN acceptance/Rejection',
    CONFIRMED: labels['status.definition.confirmed'] ?? 'VIN attached to Order',
    'READY FOR DISPATCH':
      labels['status.definition.readyForDispatch'] ??
      'Vehicles are ready for Importation and pickup.',
    FULFILLED: labels['status.definition.delivered'] ?? 'Vehicles delivered to Importer',
    DELIVERED: labels['status.definition.delivered'] ?? 'Vehicles delivered to Importer',
  };
  return map[status.toUpperCase()] ?? null;
};

const formatVehicleStatusLabel = (status: string, labels: Record<string, string>): string => {
  if (status.replace(/_/g, ' ').toUpperCase() === 'READY FOR DISPATCH') {
    return labels['status.readyForDispatch'] ?? 'Ready for Pickup';
  }

  return status
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const formatEtaDisplayText = (displayText?: string | null): string => {
  if (!displayText) {
    return '-';
  }

  return displayText.replace(/\bweeks\b/i, 'Weeks').replace(/\bweek\b/i, 'Week');
};

type CreateColumnsParams = {
  theme: 'light' | 'dark';
  onRowClick: (record: VehicleOrder) => void;
  labels: Record<string, string>;
  locale: string;
  onAcceptSingle: (order: VehicleOrder) => void;
  onRejectSingle: (order: VehicleOrder) => void;
  onGoToDelivery: (record: VehicleOrder) => void;
  onOrderConfirmationClick: (record: VehicleOrder) => void;
  isActioning?: boolean;
};

/**
 * Column definitions for the line items table
 */
export const createLineItemColumns = ({
  theme,
  onRowClick,
  labels,
  locale,
  onAcceptSingle,
  onRejectSingle,
  onGoToDelivery,
  onOrderConfirmationClick,
  isActioning = false,
}: CreateColumnsParams) => {
  const tableTheme = theme === 'dark' ? 'light' : 'dark';

  return [
    {
      key: 'statusName',
      title: labels?.['table.status'] ?? 'Status',
      width: '200px',
      sortable: true,
      render: (_: unknown, record: VehicleOrder) => {
        const status = record.statusName;
        if (!status) return <span>-</span>;
        const badge = (
          <Badge
            label={getVehicleOrderStatusLabel(status, labels)}
            type="secondary"
            color={getLineItemStatusBadgeColor(status)}
            theme={theme === 'dark' ? 'light' : 'dark'}
            minWidth="60px"
          />
        );
        const definition = getVehicleOrderStatusDefinition(status, labels);
        if (!definition) return badge;
        return (
          <Tooltip
            content={definition}
            side="top"
            theme={theme}
            maxWidth={320}
            portal
            dataTestId={`line-item-status-tooltip-${record.orderIdentifier}`}
          >
            {badge}
          </Tooltip>
        );
      },
    },
    {
      key: 'orderNumber',
      title: labels?.['table.orderNumber'] ?? 'Child Order #',
      width: '160px',
      sortable: true,
      render: (_: unknown, record: VehicleOrder) => {
        return (
          <Typography
            variant="body"
            size="small"
            color={'link'}
            theme={theme}
            onClick={() => onRowClick(record)}
          >
            {record.orderNumber ?? '-'}
          </Typography>
        );
      },
    },
    {
      key: 'model',
      title: labels?.['table.model'] ?? 'Model',
      width: '180px',
      sortable: true,
      render: (_: unknown, record: VehicleOrder) => (
        <Typography variant="body" size="small" color="black">
          {formatVehicleModelName(record.model)}
        </Typography>
      ),
    },
    {
      key: 'eta.displayText',
      title: labels?.['table.estimatedTimeOfArrival'] ?? 'Estimated Time of Arrival',
      width: '180px',
      sortable: false,
      render: (_: unknown, record: VehicleOrder) => (
        <Typography variant="body" size="medium" color="primary-1" theme={tableTheme}>
          {formatEtaDisplayText(record.eta?.displayText)}
        </Typography>
      ),
    },
    {
      key: 'trim',
      title: labels?.['table.trim'] ?? 'Trim',
      width: '140px',
      sortable: true,
      render: (_: unknown, record: VehicleOrder) => (
        <Typography variant="body" size="small" color="black">
          {record.trim ?? '-'}
        </Typography>
      ),
    },
    {
      key: 'modelYear',
      title: labels?.['table.modelYear'] ?? 'Model Year',
      width: '140px',
      sortable: true,
      render: (_: unknown, record: VehicleOrder) => (
        <Typography variant="body" size="small" color="black">
          {record.modelYear ?? '-'}
        </Typography>
      ),
    },
    {
      key: 'vin',
      title: labels?.['table.vin'] ?? 'VIN',
      width: '200px',
      sortable: true,
      render: (_: unknown, record: VehicleOrder) => (
        <Typography variant="body" size="small" color="black">
          {record.vin ?? '-'}
        </Typography>
      ),
    },
    {
      key: 'vehicleStatus',
      title: labels?.['table.vehicleStatus'] ?? 'Vehicle Status',
      width: '180px',
      sortable: true,
      render: (_: unknown, record: VehicleOrder) =>
        record.vehicleStatus ? (
          <Badge
            label={formatVehicleStatusLabel(record.vehicleStatus, labels)}
            type="secondary"
            color="gray"
            theme={theme === 'dark' ? 'light' : 'dark'}
          />
        ) : (
          <Typography variant="body" size="small" color="black">
            -
          </Typography>
        ),
    },
    {
      key: 'location',
      title: labels?.['table.vehicleLocation'] ?? 'Vehicle Location',
      width: '180px',
      sortable: true,
      render: (_: unknown, record: VehicleOrder) => (
        <Typography variant="body" size="small" color="black">
          {record.location ?? '-'}
        </Typography>
      ),
    },
    {
      key: 'totalAmount',
      title: labels?.['table.amount'] ?? 'Amount',
      width: '140px',
      sortable: true,
      render: (_: unknown, record: VehicleOrder) => (
        <Typography variant="body" size="small" color="black">
          {formatDisplayPrice(record.totalAmount ?? 0, locale)}
        </Typography>
      ),
    },
    {
      key: 'orderPreference',
      title: labels?.['table.orderType'] ?? 'Order Type',
      width: '160px',
      sortable: true,
      render: (_: unknown, record: VehicleOrder) => (
        <Typography variant="body" size="small" color="black">
          {getOrderPreferenceLabel(record.orderPreference, labels) ?? '-'}
        </Typography>
      ),
    },
    {
      key: 'actions',
      title: labels?.['table.actions'] ?? 'Actions',
      width: '150px',
      hideable: false,
      sortable: false,
      render: (_: unknown, record: VehicleOrder) => {
        if (record.statusName === 'WAITING FOR CONFIRMATION') {
          return (
            <Box className={styles.actionsCell}>
              {record.notes && (
                <Tooltip
                  content={record.notes}
                  side="top"
                  theme={theme}
                  maxWidth={360}
                  portal
                  dataTestId={`line-item-notes-tooltip-${record.orderIdentifier}`}
                >
                  <Iconography
                    name="information"
                    category="global"
                    color="gray-7"
                    theme={tableTheme}
                    dataTestId={`line-item-notes-icon-${record.orderIdentifier}`}
                  />
                </Tooltip>
              )}
              <Box pt={2} pr={2} pb={2} pl={2}>
                <Button
                  type="secondary"
                  color="neutral"
                  size="small"
                  theme={tableTheme}
                  label={labels?.['table.reject'] ?? 'Reject'}
                  dataTestId={`reject-order-${record.orderIdentifier}`}
                  gaComponent="line-items-table"
                  disabled={isActioning}
                  handleClick={() => onRejectSingle(record)}
                />
              </Box>
              <Box pt={2} pr={2} pb={2} pl={2}>
                <Button
                  color="neutral"
                  size="small"
                  theme={tableTheme}
                  label={labels?.['table.accept'] ?? 'Accept'}
                  iconLeft={{ category: 'global', name: 'check-circle' }}
                  dataTestId={`accept-order-${record.orderIdentifier}`}
                  gaComponent="line-items-table"
                  disabled={isActioning}
                  handleClick={() => onAcceptSingle(record)}
                />
              </Box>
            </Box>
          );
        }
        if (record.statusName === 'READY FOR DISPATCH') {
          return (
            <Box className={styles.actionsCell}>
              <Box pt={2} pr={2} pb={2} pl={2}>
                <Button
                  color="neutral"
                  size="small"
                  theme={tableTheme}
                  label={labels?.['table.goToDelivery'] ?? 'Go to Delivery'}
                  dataTestId={`delivery-order-${record.orderIdentifier}`}
                  gaComponent="line-items-table"
                  disabled={isActioning}
                  handleClick={() => onGoToDelivery(record)}
                />
              </Box>
              {isOrderConfirmationAvailable(record) && (
                <Box pt={2} pr={2} pb={2} pl={2}>
                  <Button
                    type="secondary"
                    color="neutral"
                    size="small"
                    theme={tableTheme}
                    label={labels?.['table.orderConfirmation'] ?? 'Order Confirmation'}
                    dataTestId={`order-confirmation-${record.orderIdentifier}`}
                    gaComponent="line-items-table"
                    handleClick={() => onOrderConfirmationClick(record)}
                  />
                </Box>
              )}
            </Box>
          );
        }
        if (isOrderConfirmationAvailable(record)) {
          return (
            <Box pt={2} pr={2} pb={2} pl={2}>
              <Button
                color="neutral"
                size="small"
                theme={tableTheme}
                label={labels?.['table.orderConfirmation'] ?? 'Order Confirmation'}
                dataTestId={`order-confirmation-${record.orderIdentifier}`}
                gaComponent="line-items-table"
                disabled={!record.orderNumber}
                handleClick={() => onOrderConfirmationClick(record)}
              />
            </Box>
          );
        }
        return null;
      },
    },
  ];
};
